/**
 * OTP + trust-device diagnostics.
 *
 * Detailed `[OTP/Debug]` logs (console.info) when ANY of:
 * - `import.meta.env.DEV` — on automatically during `npm run dev`
 * - `import.meta.env.VITE_ENV` is `testing`, `development`, or `staging` (built
 *   SPA on staging/testing hosts — production `VITE_ENV=production` stays off)
 * - `VITE_DEBUG_OTP=true` or `VITE_OTP_TRACE=true` at build time
 * - URL contains `?debugOtp=1` (or `&debugOtp=1`)
 * - `localStorage.setItem('unzippd_debug_otp', '1')` then reload
 *
 * Override dev auto-on: `VITE_DEBUG_OTP=false` in `.env`
 *
 * `[OTP]` warnings (console.warn) run on verify/login failures only.
 * A one-time `[OTP] Tip:` uses console.info so DevTools does not treat it as an error.
 */

const LS_KEY = "unzippd_debug_otp";
const HINT_KEY = "unzippd_otp_console_hint_shown";

const hasUrlDebugOtp = () => {
  try {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("debugOtp") === "1";
  } catch {
    return false;
  }
};

const isNonProductionViteEnv = () => {
  const viteEnv = String(import.meta.env?.VITE_ENV || "").toLowerCase();
  return (
    viteEnv === "testing" ||
    viteEnv === "development" ||
    viteEnv === "staging"
  );
};

export const isOtpDebugEnabled = () => {
  try {
    if (import.meta.env?.VITE_DEBUG_OTP === "false") return false;
    if (import.meta.env?.VITE_DEBUG_OTP === "true") return true;
    if (import.meta.env?.VITE_OTP_TRACE === "true") return true;
    if (import.meta.env?.DEV === true) return true;
    if (isNonProductionViteEnv()) return true;
    if (typeof window !== "undefined" && window.localStorage?.getItem(LS_KEY) === "1") {
      return true;
    }
    if (hasUrlDebugOtp()) return true;
  } catch {
    // ignore
  }
  return false;
};

/** One session reminder if detailed OTP logs are off (prod / no flag). */
export const otpConsoleHintOnce = () => {
  if (typeof window === "undefined") return;
  if (isOtpDebugEnabled()) return;
  try {
    if (window.sessionStorage?.getItem(HINT_KEY)) return;
    window.sessionStorage.setItem(HINT_KEY, "1");
    // console.info (not warn) — React DevTools' installHook often labels warn as errors
    console.info(
      "[OTP] Tip: verbose OTP trace is off on this build. To enable once: append ?debugOtp=1 to the URL and reload. Or in Console run: localStorage.setItem(\"unzippd_debug_otp\",\"1\") then reload. Then filter by [OTP/Debug].",
    );
  } catch {
    // ignore
  }
};

const preview = (value, max = 12) => {
  if (value == null || value === "") return value;
  const s = String(value);
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
};

const redactPayload = (obj) => {
  if (obj == null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(redactPayload);

  const out = { ...obj };
  const sensitiveKeys = [
    "access_token",
    "token",
    "password",
    "otp_code",
    "refresh_token",
  ];

  for (const key of Object.keys(out)) {
    const lower = key.toLowerCase();
    if (sensitiveKeys.some((k) => lower.includes(k))) {
      out[key] = out[key] ? "[redacted]" : out[key];
    } else if (key === "device_fingerprint" && typeof out[key] === "string") {
      out[key] = preview(out[key], 16);
    } else if (key === "hash" && typeof out[key] === "string") {
      out[key] = preview(out[key], 16);
    } else if (typeof out[key] === "object") {
      out[key] = redactPayload(out[key]);
    }
  }
  return out;
};

/** Verbose trace; only when `isOtpDebugEnabled()`. */
export const otpDebugLog = (label, payload) => {
  if (!isOtpDebugEnabled()) return;
  try {
    const meta = {
      label,
      origin: typeof window !== "undefined" ? window.location?.origin : "",
      href: typeof window !== "undefined" ? window.location?.href : "",
      time: new Date().toISOString(),
    };
    const body = payload === undefined ? undefined : redactPayload(payload);
    console.info("[OTP/Debug]", meta, body);
  } catch (e) {
    console.info("[OTP/Debug]", label, "(log serialization failed)", e?.message);
  }
};

/** Always logs (warn) — use on failures so something appears without enabling debug. */
export const otpProblemLog = (label, payload) => {
  try {
    const meta = { label, time: new Date().toISOString() };
    const body = payload === undefined ? undefined : redactPayload(payload);
    console.warn("[OTP]", meta, body);
  } catch (e) {
    console.warn("[OTP]", label, e?.message);
  }
};
